/**
 * Shared price extraction utilities
 *
 * Provides a single, consistent way to resolve the best available price
 * from the various data sources (Listing API vs Property API).
 *
 * Priority order:
 *  1. Listing pricing (saleDetails / rentalDetails / auctionDetails)
 *  2. Property.currentAskingPrice + currentAskingCurrency
 *  3. Property.attributeValues.askingPrice + property.currency
 *  4. null (genuinely no price)
 */

import type { Listing } from '@/lib/api/clients/listings';
import type { Property } from '@/lib/api/clients/properties';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';

export interface ExtractedPrice {
  amount: number;
  currency: string;
  frequency?: string | null;
}

/**
 * Extract the best available price from a Listing object.
 * Checks sale, rental, then auction guide price.
 */
function extractListingPrice(listing: Listing): ExtractedPrice | null {
  const sale = listing.saleDetails?.primaryPrice;
  if (sale?.amount) {
    return { amount: sale.amount, currency: sale.currency };
  }

  const rental = listing.rentalDetails?.primaryPrice;
  if (rental?.amount) {
    return {
      amount: rental.amount,
      currency: rental.currency,
      frequency: rental.frequency ?? null,
    };
  }

  const auction = listing.auctionDetails?.guidePriceMin;
  if (auction?.amount) {
    return { amount: auction.amount, currency: auction.currency };
  }

  return null;
}

/**
 * Extract the best available price from a Property's nested managedAsset listings.
 * These are the lightweight listing stubs returned by the Properties API when
 * `includeRelations=true`.
 */
function extractManagedAssetListingPrice(
  property: Property,
): ExtractedPrice | null {
  const listings = property.managedAsset?.listings;
  if (!listings?.length) return null;

  for (const listing of listings) {
    const sale = listing.saleDetails?.primaryPrice;
    if (sale?.amount) {
      return { amount: sale.amount, currency: sale.currency };
    }
    const rental = listing.rentalDetails?.primaryPrice;
    if (rental?.amount) {
      return {
        amount: rental.amount,
        currency: rental.currency,
        frequency: rental.frequency ?? null,
      };
    }
  }

  return null;
}

/**
 * Extract the best available price, checking all sources in priority order.
 *
 * @param property - The Property object (always required)
 * @param listing  - An optional full Listing object (from the Listings API)
 * @returns `{ amount, currency, frequency? }` or `null` when no price is available
 */
export function extractBestPrice(
  property: Property,
  listing?: Listing | null,
): ExtractedPrice | null {
  // 1. Full listing object (most reliable — comes from Listings API)
  if (listing) {
    const listingPrice = extractListingPrice(listing);
    if (listingPrice) return listingPrice;
  }

  // 2. Property-level asking price fields
  if (property.currentAskingPrice != null && property.currentAskingPrice > 0) {
    return {
      amount: property.currentAskingPrice,
      currency: property.currentAskingCurrency ?? property.currency ?? DEFAULT_CURRENCY,
    };
  }

  // 3. Dynamic attribute (EAV) asking price
  const attrPrice = property.attributeValues?.askingPrice?.valueNumber;
  if (attrPrice != null && attrPrice > 0) {
    return {
      amount: attrPrice,
      currency: property.currentAskingCurrency ?? property.currency ?? DEFAULT_CURRENCY,
    };
  }

  // 4. Nested managedAsset listing stubs (from includeRelations)
  const nestedPrice = extractManagedAssetListingPrice(property);
  if (nestedPrice) return nestedPrice;

  return null;
}

/**
 * Convenience: extract just amount + currency (no frequency) for contexts
 * that only need a simple price, e.g. PropertyCard, FeaturedCarousel.
 */
export function extractBestPriceSimple(
  property: Property,
  listing?: Listing | null,
): { amount: number | null; currency: string } {
  const result = extractBestPrice(property, listing);
  return {
    amount: result?.amount ?? null,
    currency:
      result?.currency ??
      property.currentAskingCurrency ??
      property.currency ??
      DEFAULT_CURRENCY,
  };
}
